import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const ShiftTeaser = () => {
  const { isDarkMode } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [glitchActive, setGlitchActive] = useState(false);
  const containerRef = useRef(null);

  // Track mouse position for 3D tilt effect
  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePos({ x, y });
  };

  // Trigger glitch effect randomly when hovered
  useEffect(() => {
    if (!isHovered) return;
    
    const glitchInterval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 150);
    }, 2000 + Math.random() * 3000);

    return () => clearInterval(glitchInterval);
  }, [isHovered]);

  // Words that flash during glitch
  const glitchWords = ['CHANGE', 'EVOLVE', 'ADAPT', 'SHIFT'];
  const [glitchWord, setGlitchWord] = useState('SHIFT');

  useEffect(() => {
    if (glitchActive) {
      setGlitchWord(glitchWords[Math.floor(Math.random() * glitchWords.length)]);
    }
  }, [glitchActive]);

  return (
    <section className="py-8 px-6 md:px-8">
      <Link
        to="/shift"
        ref={containerRef}
        className="block max-w-5xl mx-auto relative group cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setMousePos({ x: 0, y: 0 });
        }}
        onMouseMove={handleMouseMove}
        style={{
          perspective: '1000px',
        }}
      >
        {/* Main Container with 3D Tilt */}
        <div 
          className="relative bg-[#0A0A0A] rounded-3xl overflow-hidden transition-all duration-500 ease-out"
          style={{
            transform: isHovered 
              ? `rotateY(${mousePos.x * 8}deg) rotateX(${-mousePos.y * 8}deg) scale(1.02)` 
              : 'rotateY(0) rotateX(0) scale(1)',
            boxShadow: isHovered 
              ? '0 50px 100px -20px rgba(246, 205, 23, 0.3), 0 30px 60px -30px rgba(0, 0, 0, 0.5)' 
              : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }}
        >
          {/* Animated Grid Background */}
          <div className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity duration-700">
            {[...Array(15)].map((_, i) => (
              <div 
                key={`h-${i}`}
                className="absolute left-0 right-0 h-px bg-white"
                style={{ 
                  top: `${i * 7}%`,
                  transform: isHovered ? `translateX(${Math.sin(i) * 10}px)` : 'translateX(0)',
                  transition: 'transform 0.5s ease'
                }}
              />
            ))}
            {[...Array(15)].map((_, i) => (
              <div 
                key={`v-${i}`}
                className="absolute top-0 bottom-0 w-px bg-white"
                style={{ 
                  left: `${i * 7}%`,
                  transform: isHovered ? `translateY(${Math.cos(i) * 10}px)` : 'translateY(0)',
                  transition: 'transform 0.5s ease'
                }}
              />
            ))}
          </div>

          {/* Floating Geometric Shapes */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Concentric circles */}
            <div 
              className="absolute -right-20 top-1/2 -translate-y-1/2 transition-all duration-700"
              style={{
                transform: `translate(${isHovered ? -30 + mousePos.x * 20 : 0}px, ${-50 + (isHovered ? mousePos.y * 20 : 0)}%) rotate(${isHovered ? 45 : 0}deg)`,
                opacity: isHovered ? 1 : 0.3,
              }}
            >
              {[1, 0.75, 0.5, 0.25].map((scale, i) => (
                <div
                  key={i}
                  className="absolute rounded-full border border-white/10"
                  style={{
                    width: 200 * scale,
                    height: 200 * scale,
                    top: `${(1 - scale) * 100}px`,
                    left: `${(1 - scale) * 100}px`,
                  }}
                />
              ))}
              <div className="absolute w-3 h-3 bg-[#F6CD17] rounded-full" style={{ top: 97, left: 97 }} />
            </div>

            {/* Hexagon */}
            <div 
              className="absolute left-10 top-10 transition-all duration-700"
              style={{
                transform: `translate(${isHovered ? mousePos.x * -30 : 0}px, ${isHovered ? mousePos.y * -30 : 0}px) rotate(${isHovered ? -15 : 0}deg)`,
                opacity: isHovered ? 1 : 0.2,
              }}
            >
              <svg width="60" height="60" viewBox="0 0 100 100">
                <polygon 
                  points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5" 
                  fill="none" 
                  stroke="#F6CD17" 
                  strokeWidth="1"
                  className="transition-all duration-500"
                  style={{ strokeOpacity: isHovered ? 0.6 : 0.2 }}
                />
              </svg>
            </div>

            {/* Diamond */}
            <div 
              className="absolute right-1/4 bottom-8 transition-all duration-700"
              style={{
                transform: `translate(${isHovered ? mousePos.x * 25 : 0}px, ${isHovered ? mousePos.y * 25 : 0}px) rotate(${45 + (isHovered ? 15 : 0)}deg)`,
                opacity: isHovered ? 1 : 0.2,
              }}
            >
              <div 
                className="w-8 h-8 border border-white/20"
                style={{ borderColor: isHovered ? 'rgba(53, 57, 47, 0.5)' : 'rgba(255,255,255,0.1)' }}
              />
            </div>

            {/* Floating dots */}
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-[#F6CD17] transition-all duration-700"
                style={{
                  left: `${20 + i * 15}%`,
                  top: `${30 + (i % 3) * 20}%`,
                  transform: `translate(${isHovered ? mousePos.x * (20 + i * 5) : 0}px, ${isHovered ? mousePos.y * (20 + i * 5) : 0}px)`,
                  opacity: isHovered ? 0.6 : 0.1,
                }}
              />
            ))}
          </div>

          {/* Gradient Overlays */}
          <div 
            className="absolute inset-0 bg-gradient-to-r from-[#F6CD17]/0 via-transparent to-[#F6CD17]/0 transition-opacity duration-700"
            style={{ opacity: isHovered ? 0.1 : 0 }}
          />
          <div 
            className="absolute inset-0 transition-opacity duration-500"
            style={{ 
              background: `radial-gradient(circle at ${50 + mousePos.x * 30}% ${50 + mousePos.y * 30}%, rgba(246,205,23,0.15) 0%, transparent 50%)`,
              opacity: isHovered ? 1 : 0 
            }}
          />

          {/* Content */}
          <div className="relative z-10 py-16 md:py-20 px-8 md:px-12">
            <div className="max-w-3xl">
              {/* Label with animated line */}
              <div className="flex items-center gap-4 mb-6">
                <div 
                  className="h-px bg-[#F6CD17] transition-all duration-500"
                  style={{ width: isHovered ? 40 : 20 }}
                />
                <span className="text-xs uppercase tracking-widest text-white/40 group-hover:text-[#F6CD17] transition-colors duration-500">
                  Perspective
                </span>
              </div>

              {/* Main Text with Glitch Effect */}
              <div className="relative mb-8">
                <h2 
                  className="headline-xl text-[7vw] md:text-[4vw] lg:text-[3.5vw] text-white transition-all duration-300"
                  style={{
                    transform: glitchActive ? 'translateX(2px)' : 'translateX(0)',
                  }}
                >
                  We're in the middle of a{' '}
                  <span className="relative inline-block">
                    <span 
                      className="text-[#F6CD17] transition-opacity duration-100"
                      style={{ opacity: glitchActive ? 0 : 1 }}
                    >
                      shift.
                    </span>
                    {/* Glitch layers */}
                    <span 
                      className="absolute inset-0 text-cyan-400"
                      style={{ 
                        opacity: glitchActive ? 0.8 : 0,
                        transform: 'translateX(-2px)',
                      }}
                    >
                      {glitchWord.toLowerCase()}.
                    </span>
                    <span 
                      className="absolute inset-0 text-fuchsia-400"
                      style={{ 
                        opacity: glitchActive ? 0.8 : 0,
                        transform: 'translateX(2px)',
                      }}
                    >
                      {glitchWord.toLowerCase()}.
                    </span>
                  </span>
                  <br />
                  <span className="text-white/80">How you navigate it matters.</span>
                </h2>

                {/* Scan line effect on hover */}
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(transparent 50%, rgba(0,0,0,0.1) 50%)',
                    backgroundSize: '100% 4px',
                    opacity: isHovered ? 0.3 : 0,
                    transition: 'opacity 0.3s ease',
                  }}
                />
              </div>

              {/* CTA with morphing arrow */}
              <div className="flex items-center gap-4">
                <span className="text-white/60 group-hover:text-white transition-colors duration-300">
                  Explore The Shift
                </span>
                <div 
                  className="relative w-10 h-10 rounded-full border border-white/20 group-hover:border-[#F6CD17] group-hover:bg-[#F6CD17] transition-all duration-500 flex items-center justify-center overflow-hidden"
                >
                  {/* Arrow that transforms */}
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none" 
                    className="text-white/60 group-hover:text-white transition-all duration-500"
                    style={{
                      transform: isHovered ? 'translateX(0) rotate(0deg)' : 'translateX(-2px)',
                    }}
                  >
                    <path 
                      d="M3 8H13M13 8L9 4M13 8L9 12" 
                      stroke="currentColor" 
                      strokeWidth="1.5"
                      className="transition-all duration-300"
                      style={{
                        strokeDasharray: isHovered ? '20' : '10 5',
                        strokeDashoffset: isHovered ? '0' : '5',
                      }}
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Corner Accents */}
          <div className="absolute top-0 left-0 w-20 h-20">
            <div 
              className="absolute top-4 left-4 w-8 h-px bg-white/10 group-hover:bg-[#F6CD17]/50 group-hover:w-12 transition-all duration-500"
            />
            <div 
              className="absolute top-4 left-4 h-8 w-px bg-white/10 group-hover:bg-[#F6CD17]/50 group-hover:h-12 transition-all duration-500"
            />
          </div>
          <div className="absolute bottom-0 right-0 w-20 h-20">
            <div 
              className="absolute bottom-4 right-4 w-8 h-px bg-white/10 group-hover:bg-[#F6CD17]/50 group-hover:w-12 transition-all duration-500"
            />
            <div 
              className="absolute bottom-4 right-4 h-8 w-px bg-white/10 group-hover:bg-[#F6CD17]/50 group-hover:h-12 transition-all duration-500"
            />
          </div>

          {/* Noise overlay */}
          <div 
            className="absolute inset-0 pointer-events-none opacity-[0.02] group-hover:opacity-[0.04] transition-opacity"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Outer glow on hover */}
        <div 
          className="absolute -inset-1 rounded-3xl transition-opacity duration-500 -z-10"
          style={{
            background: 'linear-gradient(135deg, rgba(246,205,23,0.2) 0%, transparent 50%, rgba(53,57,47,0.2) 100%)',
            opacity: isHovered ? 1 : 0,
            filter: 'blur(20px)',
          }}
        />
      </Link>
    </section>
  );
};

export default ShiftTeaser;
